import { Plus, X } from 'lucide-react'
import { Controller, useFormContext } from 'react-hook-form'
import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ProductModel } from '@/types/model/Product'

interface VariantBarcodesProps {
  index: number
}

/**
 * The codes a scanner reads for this variant (manufacturer EAN/UPC, older packaging, in-store codes); separate from
 * the SKU. The PLU is assigned by the server and used by weight-scale labels (POS.md §18.8).
 */
export const VariantBarcodes = ({ index }: VariantBarcodesProps) => {
  const { control, getValues } = useFormContext<ProductModel.ProductFormData>()
  const [draft, setDraft] = useState('')
  const plu = getValues(`variations.${index}.plu_code`)
  const saved = !!getValues(`variations.${index}._id`)

  return (
    <Controller
      control={control}
      name={`variations.${index}.barcodes`}
      render={({ field }) => {
        const codes: string[] = field.value ?? []
        const addCode = () => {
          const code = draft.trim()
          if (code && !codes.includes(code)) field.onChange([...codes, code])
          setDraft('')
        }
        return (
          <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
            <div className="space-y-2">
              <Label htmlFor={`barcode-${index}`}>Barcodes</Label>
              <div className="flex gap-2">
                <Input
                  id={`barcode-${index}`}
                  placeholder="Scan or type a barcode"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    // Scanners end with Enter; keep it from submitting the form.
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addCode()
                    }
                  }}
                />
                <Button type="button" variant="secondary" onClick={addCode}>
                  <Plus />
                  Add
                </Button>
              </div>
              {codes.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {codes.map((code) => (
                    <Badge key={code} tone="soft" className="gap-1 pr-1 font-mono text-ink">
                      {code}
                      <button
                        type="button"
                        aria-label={`Remove ${code}`}
                        className="grid size-4 place-items-center rounded-sm text-text-2 transition-colors hover:bg-selected hover:text-ink"
                        onClick={() => field.onChange(codes.filter((c) => c !== code))}
                      >
                        <X className="size-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>PLU</Label>
              <p className="flex h-9 items-center font-mono text-sm text-text-2">{plu ?? (saved ? 'Assigning…' : 'After saving')}</p>
            </div>
          </div>
        )
      }}
    />
  )
}
